import { Clip } from '../types/clip-types';
import { ElementType } from '../types/Element/Type';
import { updateElementTrackTransitions } from '../types/RemotionPlayerConfigTypes/Animations/Transitions/Transition.utils';
import { Element } from '../types/RemotionPlayerConfigTypes/Element/Element';
import { ImageElement } from '../types/RemotionPlayerConfigTypes/Element/ImageElement';
import { VideoElement } from '../types/RemotionPlayerConfigTypes/Element/VideoElement';
import { ElementsTrack } from '../types/RemotionPlayerConfigTypes/Track/ElementsTrack';
import { TrackType } from '../types/Track/Type';
import { getAbsolutePlayerTimePure, getPlayerTimeFromAbsoluteTimePure } from './timeUtils';

export const isVideoElement = (element: Element): element is VideoElement => {
  return element.type === ElementType.VIDEO;
};

export const isImageElement = (element: Element): element is ImageElement => {
  return element.type === ElementType.IMAGE;
};
export const shiftElements = (track: ElementsTrack, frameOffset: number) => {
  if (!Array.isArray(track.elements)) {
    return;
  }

  track.elements = track.elements
    .map(el => {
      const newFromFrame = Math.max(0, el.fromFrame + frameOffset);
      const newToFrame = Math.max(0, el.toFrame + frameOffset);

      return {
        ...el,
        fromFrame: newFromFrame,
        toFrame: newToFrame
      };
    })
    .filter(el => {
      const hasValidDuration = el.toFrame > el.fromFrame;
      return hasValidDuration;
    });
};
export const adjustElementsWithDeletes = ({
  track,
  fps,
  prevClip,
  newClip,
  isSplitsTranslationEnabled,
  zoomLevel
}: {
  track: ElementsTrack;
  fps: number;
  prevClip: Clip;
  newClip: Clip;
  isSplitsTranslationEnabled: boolean;
  zoomLevel: number;
}) => {
  if (!Array.isArray(track.elements)) {
    return;
  }

  track.elements = track.elements
    .map(el => ({
      ...el,
      fromFrame: convertFrameTime({
        frame: el.fromFrame,
        fps,
        prevClip,
        newClip,
        isSplitsTranslationEnabled,
        zoomLevel
      }),
      toFrame: convertFrameTime({ frame: el.toFrame, fps, prevClip, newClip, isSplitsTranslationEnabled, zoomLevel })
    }))
    .filter(el => el.fromFrame !== el.toFrame);
  track.elements = updateElementTrackTransitions(track, fps).elements;
};

export function convertFrameTime({
  frame,
  fps,
  prevClip,
  newClip,
  isSplitsTranslationEnabled,
  zoomLevel
}: {
  frame: number;
  fps: number;
  prevClip: Clip;
  newClip: Clip;
  isSplitsTranslationEnabled: boolean;
  zoomLevel: number;
}): number {
  const timeInSeconds = frame / fps;
  const absoluteTime = getAbsolutePlayerTimePure({
    time: timeInSeconds,
    clipData: prevClip,
    minSegmentWidth: -Infinity,
    isSplitsTranslationEnabled,
    zoomLevel
  });
  const newTime = getPlayerTimeFromAbsoluteTimePure({
    absoluteTime,
    clipData: newClip,
    minSegmentWidth: -Infinity,
    isSplitsTranslationEnabled,
    zoomLevel
  });
  return newTime * fps;
}
export const updatePlayerConfigWithDeletes = ({
  playerConfig,
  fps,
  prevClip,
  newClip,
  isSplitsTranslationEnabled,
  zoomLevel
}: {
  playerConfig: ElementsTrack[];
  fps: number;
  prevClip: Clip;
  newClip: Clip;
  isSplitsTranslationEnabled: boolean;
  zoomLevel: number;
}): ElementsTrack[] => {
  const prevMeta = prevClip?.asset_metadata;
  const newMeta = newClip?.asset_metadata;

  if (prevMeta?.start !== newMeta?.start || prevMeta?.end !== newMeta?.end) {
    return playerConfig;
  }

  const updatedConfig: ElementsTrack[] = structuredClone(playerConfig);

  const introDelta = (newMeta?.introDuration ?? 0) - (prevMeta?.introDuration ?? 0);

  // Update elements tracks
  for (const track of updatedConfig) {
    if (String(track.type) !== String(TrackType.ELEMENTS)) {
      continue;
    }

    if (introDelta !== 0) {
      shiftElements(track, introDelta * fps);
    } else {
      adjustElementsWithDeletes({ track, fps, prevClip, newClip, isSplitsTranslationEnabled, zoomLevel });
    }
  }

  return updatedConfig;
};
